import { copilotContext as e, copilotInstructions as t, copilotTools as n, uniqueId as r } from "./global-state.js";
import { atom as i, computed as a, map as o } from "nanostores";
//#region src/session.ts
function s() {
	return Math.floor(Date.now() / 1e3);
}
var c = class extends Error {
	constructor(e = "The agent is still working on the previous turn.", t) {
		super(e), this.name = "CopilotActiveExchangeError", this.cause = t?.cause;
	}
}, l = {
	idle: ["initializing", "ending"],
	initializing: ["ready", "error"],
	ready: ["sending", "ending"],
	sending: ["receiving", "recovering"],
	receiving: ["ready", "recovering"],
	recovering: ["ready", "ending"],
	ending: ["ended", "error"],
	error: ["initializing", "ending"],
	ended: []
}, u = "The previous tool call did not complete. The action may not have run. Tell the user the action did not complete and offer to try again — do not claim success or failure.", d = 2e3, f = 6e4, p = 1e4, m = 1e4, h = 18e5;
function g(e) {
	return e < f ? d : p;
}
var _ = "cio-copilot-approval-mode";
function v() {
	try {
		let e = window.localStorage.getItem(_);
		return e === "ask" || e === "auto" ? e : null;
	} catch {
		return null;
	}
}
function y(e) {
	try {
		window.localStorage.setItem(_, e);
	} catch {}
}
function b(e, t) {
	if (e === t) return e;
	if (!l[e]) throw Error(`Unknown status: ${e}`);
	if (l[e].includes(t)) return t;
	throw Error(`Invalid status transition: ${e} -> ${t}`);
}
var x = class {
	get id() {
		return this.__id.get();
	}
	set id(e) {
		this.__id.set(e == null ? void 0 : String(e));
	}
	constructor(r) {
		this.__id = i(void 0), this.config = {
			includeGlobalContext: !0,
			includeGlobalTools: !0,
			includeGlobalInstructions: !0
		}, this.__status = i("idle"), this.__messages = i([]), this.__meta = i({}), this.__uploadFiles = i([]), this.__queuedSends = i([]), this.__queuedMessages = a(this.__queuedSends, (e) => e.flatMap((e) => e.messages)), this.__restoredMessages = i([]), this.__lastTurnStopped = i(!1), this.__orphanedToolCallIds = i(/* @__PURE__ */ new Set()), this.__abortController = null, this.__recoveryStopTimeout = null, this.__wakeRecoveryPoll = null, this.__recoveryGeneration = 0, this.__enableWebSearch = i(!0), this.__approvalMode = i(v() ?? "ask"), this.__approvalModeAvailable = i(!1), this.__serverSideTools = o({}), this.__sessionInstructions = o({}), this.__sessionTools = o({}), this.__sessionContext = o({}), this.__tools = a([
			this.__serverSideTools,
			n,
			this.__sessionTools
		], (e, t, n) => {
			let r = {}, i = {
				...this.config.includeGlobalTools === !0 ? t : {},
				...n
			};
			for (let t of Object.values(e)) if (t != null) {
				let e = i[t.name];
				r[t.name] = {
					...e?.type === "server" ? e : void 0,
					...t
				};
			}
			for (let e of Object.values(i)) e != null && e.type === "client" && r[e.name] == null && (r[e.name] = e);
			return r;
		}), this.__statefulMessages = a([
			this.__messages,
			this.__tools,
			this.__orphanedToolCallIds
		], (e, t, n) => T(e, t, n, (e, t, n) => this.respondToToolCall(e, t, n))), this.__instructions = a([t, this.__sessionInstructions], (e, t) => ({
			...this.config.includeGlobalInstructions === !0 ? e : {},
			...t
		})), this.__context = a([e, this.__sessionContext], (e, t) => ({
			...this.config.includeGlobalContext === !0 ? e : {},
			...t
		})), this.__respondedToolCallIds = /* @__PURE__ */ new Set(), this.__redispatchingToolCallIds = /* @__PURE__ */ new Set(), this.__toolsUnsub = null, this.__suggestionsRefreshTrigger = i([]), r && (this.config = {
			...this.config,
			...r
		});
	}
	get status() {
		return this.__status.get();
	}
	get isEnding() {
		return this.status === "ending";
	}
	get isEnded() {
		return this.status === "ended";
	}
	setStatus(e) {
		let t = this.status, n = b(t, e);
		n === "recovering" && t !== "recovering" && (this.__recoveryGeneration += 1), n !== "recovering" && (this.__recoveryStopTimeout != null && (clearTimeout(this.__recoveryStopTimeout), this.__recoveryStopTimeout = null), this.__wakeRecoveryPoll?.()), this.__status.set(n);
	}
	addInstruction(e) {
		let t = r();
		return this.__sessionInstructions.setKey(t, e), () => {
			this.__sessionInstructions.setKey(t, void 0);
		};
	}
	addTool(e) {
		return this.__sessionTools.setKey(e.name, e), () => {
			this.__sessionTools.setKey(e.name, void 0);
		};
	}
	addContext(e) {
		let t = r();
		return this.__sessionContext.setKey(t, e), () => {
			this.__sessionContext.setKey(t, void 0);
		};
	}
	setSessionMeta(e) {
		this.__meta.set(e);
	}
	getSessionMeta() {
		return this.__meta.get();
	}
	setWebSearchEnabled(e) {
		this.__enableWebSearch.set(e);
	}
	getWebSearchEnabled() {
		return this.__enableWebSearch.get();
	}
	setApprovalMode(e) {
		this.__approvalMode.set(e), y(e);
	}
	setApprovalModeAvailable(e) {
		this.__approvalModeAvailable.set(e);
	}
	getApprovalMode() {
		return this.__approvalModeAvailable.get() ? this.__approvalMode.get() : "ask";
	}
	setMessageMeta(e, t) {
		let n = this.__messages.get().map((n) => n.role === "model" && n.type === "meta" && "meta" in n && n.meta?.id === e ? {
			...n,
			meta: t
		} : n);
		this.__messages.set(n);
	}
	respondToToolCall(e, t, n) {
		this.__respondedToolCallIds.has(e) || (this.__respondedToolCallIds.add(e), this.sendMessage([{
			role: "user",
			type: "tool_response",
			tool_responses: [{
				tool_call_id: e,
				name: t,
				response: {
					result: n,
					error: null
				},
				finished: !0
			}],
			timestamp: s()
		}]).catch(() => {
			this.__respondedToolCallIds.delete(e);
		}));
	}
	clearQueuedMessages() {
		let e = this.__queuedMessages.get();
		return this.__queuedSends.set([]), e;
	}
	clearRestoredMessages() {
		this.__restoredMessages.set([]);
	}
	surfaceQueuedMessagesForRestore() {
		let e = this.clearQueuedMessages();
		e.length > 0 && this.__restoredMessages.set([...this.__restoredMessages.get(), ...e]);
	}
	settleReady() {
		this.markOrphanedToolCalls(), this.status === "sending" && this.setStatus("receiving"), this.setStatus("ready");
	}
	settleReadyAfterFailure() {
		this.settleReady(), this.surfaceQueuedMessagesForRestore();
	}
	scheduleRecoveryStopFallback() {
		this.__recoveryStopTimeout ??= setTimeout(() => {
			this.__recoveryStopTimeout = null, this.status === "recovering" && this.settleReadyAfterFailure();
		}, m), this.__wakeRecoveryPoll?.();
	}
	waitForRecoveryPoll(e) {
		return new Promise((t) => {
			let n = setTimeout(() => {
				this.__wakeRecoveryPoll = null, t(!0);
			}, e);
			this.__wakeRecoveryPoll = () => {
				clearTimeout(n), this.__wakeRecoveryPoll = null, t(!1);
			};
		});
	}
	queueSend(e, t) {
		let n = this.__queuedSends.get(), r = n[n.length - 1];
		if (t.length === 0 && r?.instructions.length === 0) {
			this.__queuedSends.set([...n.slice(0, -1), {
				...r,
				messages: [...r.messages, ...e]
			}]);
			return;
		}
		this.__queuedSends.set([...n, {
			messages: e,
			instructions: t
		}]);
	}
	queueLatestUnsentPrompt(e, t) {
		let n = N(e);
		if (n.length === 0) return;
		let r = this.__queuedSends.get().filter((e) => !M(e.messages));
		this.__queuedSends.set([...r, {
			messages: n,
			instructions: t
		}]);
	}
	hasQueuedUnsentPrompt() {
		return this.__queuedSends.get().some((e) => M(e.messages));
	}
	dropQueuedPromptsAlreadyInMessages(e) {
		let t = this.__queuedSends.get(), n = t.filter((t) => !M(t.messages) || !I(e, t.messages));
		n.length !== t.length && this.__queuedSends.set(n);
	}
	async stop() {
		if (this.status !== "sending" && this.status !== "receiving" && this.status !== "recovering") return { messages: [] };
		if (this.__lastTurnStopped.set(!0), this.status === "recovering") {
			let e = this.clearQueuedMessages(), t = this.__recoveryGeneration, n = window.__hydra_copilot__?.stopExchange;
			if (n == null || this.id == null) return this.settleReady(), { messages: e };
			this.scheduleRecoveryStopFallback();
			try {
				await n({ id: this.id });
			} catch (n) {
				return this.status === "recovering" && this.__recoveryGeneration === t ? (this.settleReadyAfterFailure(), {
					messages: e,
					error: n instanceof Error ? n : Error(String(n))
				}) : { messages: e };
			}
			return { messages: e };
		}
		this.__abortController?.abort();
		let e = this.clearQueuedMessages(), t = window.__hydra_copilot__?.stopExchange;
		if (t != null && this.id != null) try {
			await t({ id: this.id });
		} catch (t) {
			return {
				messages: e,
				error: t instanceof Error ? t : Error(String(t))
			};
		}
		return { messages: e };
	}
	refreshSuggestions(e) {
		this.__suggestionsRefreshTrigger.set([{ instruction: e }]);
	}
	async initialize(e) {
		if (this.status !== "idle" && this.status !== "error") return this.id;
		this.setStatus("initializing");
		try {
			let t = window.__hydra_copilot__?.startSession;
			if (t == null) throw Error("Copilot session not configured. Please set the Copilot configuration first.");
			if (e == null) {
				let e = await t();
				if (e.id == null) throw Error("Failed to start session, no session ID returned");
				this.id = e.id, this.__serverSideTools.set(G(e.tools ?? []));
			} else {
				this.__approvalMode.set("ask"), this.id = e;
				let t = await this.loadSession();
				this.__messages.set(t.messages ?? []), this.__meta.set(t.meta ?? {}), this.__serverSideTools.set(G(t.tools ?? [])), this.__orphanedToolCallIds.set(/* @__PURE__ */ new Set());
			}
			return this.setStatus("ready"), this.executePendingClientToolCalls(), this.id;
		} catch (e) {
			throw this.setStatus("error"), e;
		}
	}
	async end() {
		this.setStatus("ending");
		try {
			let e = window.__hydra_copilot__?.endSession;
			if (e == null) throw Error("Copilot session not configured. Please set the Copilot configuration first.");
			this.id != null && await e(this.id), this.__messages.set([]), this.__queuedSends.set([]), this.__restoredMessages.set([]), this.__respondedToolCallIds.clear(), this.__redispatchingToolCallIds.clear(), this.teardownToolsSubscription(), this.__orphanedToolCallIds.set(/* @__PURE__ */ new Set()), this.id = void 0, this.__sessionInstructions.set({}), this.__sessionTools.set({}), this.__sessionContext.set({}), this.__serverSideTools.set({}), this.__meta.set({}), this.setStatus("ended");
		} catch (e) {
			throw this.setStatus("error"), e;
		}
	}
	markOrphanedToolCalls() {
		let e = this.__messages.get(), t = this.__tools.get(), n = /* @__PURE__ */ new Set();
		for (let t of e) if (t.role === "user" && t.type === "tool_response") for (let e of t.tool_responses) n.add(e.tool_call_id);
		let r = new Set(this.__orphanedToolCallIds.get()), i = !1;
		for (let a of e) if (!(a.role !== "model" || a.type !== "tool_call")) for (let e of a.tool_calls) n.has(e.id) || e.needs_approval !== !0 && t[e.name]?.humanInTheLoop !== !0 && (r.has(e.id) || (r.add(e.id), i = !0));
		i && this.__orphanedToolCallIds.set(r);
	}
	executePendingClientToolCalls() {
		if (this.status !== "ready") return Promise.resolve();
		let e = this.__messages.get(), t = this.__tools.get(), n = this.__orphanedToolCallIds.get(), r = /* @__PURE__ */ new Set();
		for (let t of e) if (t.role === "user" && t.type === "tool_response") for (let e of t.tool_responses) r.add(e.tool_call_id);
		let i = [], a = !1;
		for (let o of e) if (!(o.role !== "model" || o.type !== "tool_call")) for (let e of o.tool_calls) {
			if (r.has(e.id) || n.has(e.id) || this.__redispatchingToolCallIds.has(e.id) || e.is_client_tool !== !0 || e.needs_approval === !0) continue;
			let o = t[e.name];
			if (o == null) {
				a = !0;
				continue;
			}
			if (o.type !== "server" && o.humanInTheLoop !== !0) {
				if (o.handler == null) {
					a = !0;
					continue;
				}
				k(o.needsApproval, e.args) || i.push({
					id: e.id,
					name: e.name,
					args: e.args ?? {}
				});
			}
		}
		if (a ? this.ensureToolsSubscription() : this.teardownToolsSubscription(), i.length === 0) return Promise.resolve();
		for (let e of i) this.__redispatchingToolCallIds.add(e.id);
		return (async () => {
			try {
				let e = await Promise.all(i.map(async (e) => {
					let t = this.__tools.get()[e.name];
					try {
						if (t == null || t.type === "server" || t.humanInTheLoop === !0 || t.handler == null) throw Error(`Tool ${e.name} does not have a handler defined.`);
						let n = await t.handler({
							args: e.args,
							session: this,
							toolCallId: e.id
						}) ?? null;
						return {
							tool_call_id: e.id,
							name: e.name,
							response: {
								result: n,
								error: null
							},
							finished: !0
						};
					} catch (t) {
						return {
							tool_call_id: e.id,
							name: e.name,
							response: {
								result: null,
								error: `Error calling tool ${e.name}: ${t instanceof Error ? t.message : String(t)}`
							},
							finished: !0
						};
					}
				}));
				await this.sendMessage([{
					role: "user",
					type: "tool_response",
					tool_responses: e,
					timestamp: s()
				}]);
			} catch {} finally {
				for (let e of i) this.__redispatchingToolCallIds.delete(e.id);
			}
		})();
	}
	ensureToolsSubscription() {
		this.__toolsUnsub ??= this.__tools.listen(() => {
			this.executePendingClientToolCalls();
		});
	}
	teardownToolsSubscription() {
		this.__toolsUnsub != null && (this.__toolsUnsub(), this.__toolsUnsub = null);
	}
	dispose() {
		this.teardownToolsSubscription();
	}
	getPendingApprovals() {
		let e = this.__messages.get(), t = /* @__PURE__ */ new Set();
		for (let n of e) if (n.role === "user" && n.type === "tool_response") for (let e of n.tool_responses) t.add(e.tool_call_id);
		let n = [];
		for (let r of e) if (r.role === "model" && r.type === "tool_call") for (let e of r.tool_calls) e.needs_approval === !0 && !t.has(e.id) && n.push({
			id: e.id,
			name: e.name
		});
		return n;
	}
	async sendMessage(e, t = {}) {
		let n = t.instructions ?? [], r = typeof e == "string" ? [{
			role: "user",
			type: "text",
			text: e,
			timestamp: s()
		}] : [...e];
		r = P(r);
		let i = window.__hydra_copilot__?.sendStreamingMessage;
		if (i == null) throw Error("Copilot session not configured. Please set the Copilot configuration first.");
		if ((this.status === "idle" || this.status === "error") && await this.initialize(), this.status === "initializing" && await this.waitUntilReady(), this.status !== "ready") {
			if (this.status === "sending" || this.status === "receiving" || this.status === "recovering") {
				this.status === "recovering" && M(r) ? this.queueLatestUnsentPrompt(r, n) : this.queueSend(r, n);
				return;
			}
			throw Error(`Cannot send message while session is in "${this.status}" state.`);
		}
		if (!r.some((e) => e.type === "tool_response")) {
			let e = this.getPendingApprovals();
			e.length > 0 && (r = [W(e), ...r]);
		}
		let a = null, o = !1, c = !1;
		try {
			this.setStatus("sending"), this.__lastTurnStopped.set(!1), this.__abortController = new AbortController();
			let e = r.map((e) => ({
				...e,
				timestamp: e.timestamp || s()
			}));
			for (; e.length > 0;) {
				this.__messages.set([...this.__messages.get(), ...e]);
				let t = [], r, a = (n) => {
					try {
						this.setStatus("receiving"), n.type === "meta" && (o = !0);
						let r = n;
						if (n.role === "model" && n.type === "tool_call") {
							let e = n.tool_calls.map((e) => {
								if (e.is_client_tool === !0 && !e.needs_approval) {
									let t = this.__tools.get()[e.name];
									if (k(t?.needsApproval, e.args)) return {
										...e,
										needs_approval: !0
									};
								}
								return e;
							});
							r = {
								...n,
								tool_calls: e
							};
						}
						if (this.__messages.set([...this.__messages.get(), r]), r.role === "model" && r.type === "tool_call") for (let e of r.tool_calls) {
							if (e.needs_approval || e.is_client_tool !== !0) continue;
							let n = this.__tools.get()[e.name];
							n?.humanInTheLoop !== !0 && t.push((async () => {
								try {
									if (n?.type === "server") throw Error(`Tool ${e.name} is a server-side tool and cannot be called on the client.`);
									if (n?.handler == null) throw Error(`Tool ${e.name} does not have a handler defined.`);
									let t = await n.handler({
										args: e.args,
										session: this,
										toolCallId: e.id
									}) ?? null;
									return {
										tool_call_id: e.id,
										name: e.name,
										response: {
											result: t,
											error: null
										},
										finished: !0
									};
								} catch (t) {
									return {
										tool_call_id: e.id,
										name: e.name,
										response: {
											result: null,
											error: `Error calling tool ${e.name}: ${t instanceof Error ? t.message : String(t)}`
										},
										finished: !0
									};
								}
							})());
						}
						if (n.role === "user" && n.type === "tool_response") for (let t of n.tool_responses) {
							let n = this.__tools.get()[t.name], r = j([...this.__messages.get(), ...e], t.tool_call_id);
							n?.type === "server" && n?.handler != null && t.finished === !0 && n.handler({
								args: r?.args || {},
								session: this,
								response: t.response,
								toolCallId: t.tool_call_id,
								wasApproved: t.was_approved === !0
							});
						}
					} catch (e) {
						throw r = e, e;
					}
				}, l = {
					id: this.id,
					messages: e,
					tools: E(this.__tools.get()),
					instructions: [...D(this.__instructions.get()), ...n],
					context: O(this.__context.get()),
					onMessage: a,
					signal: this.__abortController?.signal,
					enable_web_search: this.getWebSearchEnabled(),
					approval_mode: this.getApprovalMode()
				};
				try {
					await i(l);
				} catch (t) {
					if (V(t)) throw t;
					if (r !== void 0) throw r;
					H(t) && (c = !0, N(e).length > 0 && !this.hasQueuedUnsentPrompt() && this.queueLatestUnsentPrompt(e, n));
					break;
				}
				if (t.length === 0) {
					e = [];
					continue;
				}
				e = P([{
					role: "user",
					type: "tool_response",
					tool_responses: await Promise.all(t),
					timestamp: s()
				}]);
			}
			if (!o || c) {
				this.setStatus("recovering"), this.recoverInterruptedStream().catch(() => {
					this.status === "recovering" && this.settleReadyAfterFailure();
				});
				return;
			}
			this.markOrphanedToolCalls(), this.setStatus("ready"), a = B(this.__queuedSends);
		} catch (e) {
			if (V(e)) {
				this.settleReady();
				return;
			}
			throw this.settleReadyAfterFailure(), e;
		} finally {
			this.__abortController = null;
		}
		a != null && a.messages.length > 0 && this.sendMessage(a.messages, { instructions: a.instructions }).catch(() => {});
	}
	async recoverInterruptedStream() {
		if (this.id == null || window.__hydra_copilot__?.getSession == null) {
			this.settleReadyAfterFailure();
			return;
		}
		let e = (e) => {
			for (let t = e.length - 1; t >= 0; t--) {
				let n = e[t];
				if (n.type === "meta") return n.meta?.id;
			}
		}, t = e(this.__messages.get()), n = (e) => F(e, t).filter((e) => e.type === "meta" || e.type === "error" && e.client_generated !== !0).length, r = n(this.__messages.get()), i = 0;
		for (; i < h;) {
			let a = this.__lastTurnStopped.get() ? d : g(i);
			if (await this.waitForRecoveryPoll(a) && (i += a), this.status !== "recovering") return;
			let o;
			try {
				let e = await this.loadSession();
				o = {
					messages: e.messages ?? [],
					meta: e.meta ?? {}
				};
			} catch {
				continue;
			}
			let s = e(o.messages), c = o.messages[o.messages.length - 1]?.type, l = s == null ? c === "meta" : s !== t, u = c === "error" && n(o.messages) > r;
			if (l || u) {
				await this.adoptRecoveredSession(o, t);
				return;
			}
		}
		this.status === "recovering" && (this.__messages.set([...this.__messages.get(), {
			role: "model",
			type: "error",
			error: "The connection was interrupted. Please try sending your message again.",
			client_generated: !0,
			timestamp: s()
		}]), this.settleReadyAfterFailure());
	}
	async adoptRecoveredSession(e, t) {
		if (this.status !== "recovering") return;
		this.__messages.set(e.messages), this.__meta.set(e.meta), this.__orphanedToolCallIds.set(/* @__PURE__ */ new Set()), this.dropQueuedPromptsAlreadyInMessages(F(e.messages, t));
		let n = this.__lastTurnStopped.get();
		n && (this.markOrphanedToolCalls(), this.teardownToolsSubscription()), this.setStatus("ready"), n || await this.executePendingClientToolCalls(), this.drainAndSendQueuedMessages();
	}
	drainAndSendQueuedMessages() {
		let e = B(this.__queuedSends);
		e.messages.length !== 0 && this.sendMessage(e.messages, { instructions: e.instructions }).catch(() => {});
	}
	async loadSession() {
		let e = window.__hydra_copilot__?.getSession;
		if (e == null) throw Error("Copilot session not configured. Please set the Copilot configuration first.");
		if (this.id == null) throw Error("Session not started");
		return await e(this.id);
	}
	async waitUntilReady() {
		if (this.status !== "ready") {
			if (this.status === "idle") {
				await this.initialize();
				return;
			}
			if (this.status === "error") throw Error("Session is in error state");
			if (this.status === "ending" || this.status === "ended") throw Error(`Cannot wait for session in "${this.status}" state`);
			return new Promise((e, t) => {
				let n = null, r = null, i = () => {
					n !== null && (clearTimeout(n), n = null), r !== null && (r(), r = null);
				};
				r = this.__status.subscribe((n) => {
					n === "ready" ? (i(), e()) : n === "error" ? (i(), t(/* @__PURE__ */ Error("Session failed while waiting for ready"))) : (n === "ending" || n === "ended") && (i(), t(/* @__PURE__ */ Error("Session ended while waiting for ready")));
				}), n = setTimeout(() => {
					i(), t(/* @__PURE__ */ Error("Waiting for session to be ready timed out - exceeded 30 seconds"));
				}, 3e4);
			});
		}
	}
	async getUploadSignature(e) {
		if (!this.id) throw Error("Session ID not available - ensure session is ready before calling getUploadSignature");
		let t = window.__hydra_copilot__;
		if (!t?.getUploadSignature) throw Error("File upload not configured. Please provide getUploadSignature in setCopilotConfig.");
		return await t.getUploadSignature({
			id: this.id,
			size: e.size,
			mime_type: e.mime_type
		});
	}
	async getFileDownloadUrl(e) {
		if (!this.id) throw Error("Session ID not available - ensure session is ready before calling getFileDownloadUrl");
		let t = window.__hydra_copilot__;
		if (!t?.getFileDownloadUrl) throw Error("File download not configured. Please provide getFileDownloadUrl in setCopilotConfig.");
		return (await t.getFileDownloadUrl({
			id: this.id,
			filename: e
		})).url;
	}
	addUploadFiles(e) {
		let t = this.__uploadFiles.get();
		this.__uploadFiles.set([...t, ...e]);
	}
	updateUploadFile(e, t) {
		let n = this.__uploadFiles.get().map((n) => n.id === e ? {
			...n,
			...t
		} : n);
		this.__uploadFiles.set(n);
	}
	removeUploadFile(e) {
		let t = this.__uploadFiles.get().filter((t) => t.id !== e);
		this.__uploadFiles.set(t);
	}
	clearUploadFiles() {
		this.__uploadFiles.set([]);
	}
	getPendingUploadFiles() {
		return this.__uploadFiles.get().filter((e) => e.status === "pending");
	}
};
function S(e) {
	return typeof e == "string" ? e : typeof e == "number" || typeof e == "boolean" ? String(e) : Array.isArray(e) || typeof e == "object" ? JSON.stringify(e) : String(e);
}
function C(e) {
	let t = /* @__PURE__ */ new Map(), n = /* @__PURE__ */ new Set(), r = /* @__PURE__ */ new Set();
	return e.forEach((i, a) => {
		if (i.was_approved !== !0 || i.response == null || i.approval_request != null) return;
		let o = e.findIndex((e, t) => t !== a && !n.has(t) && e.name === i.name && e.needs_approval === !0 && e.approval_request == null && JSON.stringify(e.args) === JSON.stringify(i.args));
		o !== -1 && (t.set(o, {
			response: i.response,
			status: i.status
		}), n.add(o), r.add(a));
	}), {
		approvalResponses: t,
		claimedExecutions: r
	};
}
function w(e) {
	let t = Array.isArray(e) ? e : [e];
	for (let e of t) {
		let t = e;
		if (typeof t == "string") try {
			t = JSON.parse(t);
		} catch {
			continue;
		}
		if (typeof t == "object" && t && "exit_code" in t) {
			let e = t.exit_code;
			return typeof e == "number" && e !== 0;
		}
	}
	return !1;
}
function T(e, t, n, r) {
	let i = [], a = [], o = [], s = [], c = {}, l = e.filter((e) => {
		if (e.role === "user" && e.type === "tool_response") {
			for (let t of e.tool_responses) t.tool_call_id != null && (c[t.tool_call_id] ?? (c[t.tool_call_id] = []), c[t.tool_call_id].push(t));
			return !1;
		}
		return e.type === "text" || e.type === "content" || e.type === "tool_call" || e.type === "meta" || e.type === "error" || e.type === "compaction_start" || e.type === "compaction_complete";
	});
	function d() {
		let e = a.filter((e) => e.type === "text"), t = a.find((e) => e.type === "meta" && "meta" in e);
		if (a = [], e.length === 0) return;
		let n = e.map((e) => e.text).join(""), r = t?.meta ?? {}, o = e[0]?.thought, s;
		if (o === !0) {
			let t = i[i.length - 1];
			s = t != null && t.timestamp != null ? Math.max(0, e[e.length - 1].timestamp - t.timestamp) : 0;
		}
		i.push({
			role: "model",
			type: "text",
			text: n,
			timestamp: e[0].timestamp,
			meta: r,
			...o === !0 && { thought: o },
			...s != null && { duration: s }
		}), a = [];
	}
	function f() {
		if (o.length === 0) return;
		let e = o.find((e) => e.type === "text"), t = o.filter((e) => e.type === "content"), n = o[0];
		i.push({
			role: "user",
			type: "text",
			text: e?.text ?? "",
			attachments: t.map((e) => ({
				content: e.content,
				mime_type: e.mime_type,
				display_name: e.display_name
			})),
			timestamp: n.timestamp ?? 0
		}), o = [];
	}
	function p(e) {
		let i = c[e.id], a = t[e.name], o = e.needs_approval === !0 || e.is_client_tool === !0 && k(a?.needsApproval, e.args), s = o ? {
			...e,
			needs_approval: !0
		} : e;
		if (i == null || i.length === 0) {
			let t = e.is_client_tool === !0 && a?.humanInTheLoop === !0;
			return n.has(e.id) && !o && !t ? {
				...s,
				status: "error",
				response: {
					result: null,
					error: u
				}
			} : {
				...s,
				status: "executing",
				...t && { respond: (t) => r(e.id, e.name, t) }
			};
		}
		let l = i.find((e) => e.response.error != null && e.response.error !== ""), d = i.some((e) => e.finished), f = i.some((e) => e.was_approved === !0);
		if (l != null) return {
			...s,
			status: "error",
			response: l.response,
			...f && { was_approved: !0 }
		};
		let p = a?.type === "server" && a?.isStreaming === !0;
		if (d && i.length === 1 && !p) {
			let t = e.name === "bash" && w(i[0].response.result);
			return {
				...s,
				status: t ? "error" : "success",
				response: i[0].response,
				...f && { was_approved: !0 }
			};
		}
		let m = i.map((e) => e.response.result), h = d && e.name === "bash" && w(m);
		return {
			...s,
			status: h ? "error" : d ? "success" : "executing",
			response: {
				result: m,
				error: null
			},
			...f && { was_approved: !0 }
		};
	}
	function m() {
		if (s.length === 0) return;
		let e = s.flatMap((e) => e.tool_calls.map(p)), t = /* @__PURE__ */ new Map();
		for (let n of e) t.set(n.id, n);
		let n = Array.from(t.values()), { approvalResponses: r, claimedExecutions: a } = C(n), o = n.map((e, t) => {
			let n = r.get(t);
			return n == null ? e : {
				...e,
				response: n.response,
				status: n.status
			};
		}).filter((e, t) => !(a.has(t) || e.was_approved === !0 && e.approval_request == null));
		o.length > 0 && i.push({
			role: "model",
			type: "tool_call",
			tool_calls: o,
			timestamp: s[0].timestamp
		}), s = [];
	}
	for (let e of l) if (e.role === "model" && e.type === "text") {
		f(), m();
		let t = a.some((e) => e.type === "text"), n = e.thought === !0, r = a.some((e) => e.type === "text" && "thought" in e && e.thought === !0);
		t && (n && !r || !n && r) && d(), a.push(e);
	} else if (e.role === "user" && (e.type === "text" || e.type === "content")) d(), m(), e.type === "text" && o.some((e) => e.type === "text") && f(), o.push(e);
	else if (e.role === "model" && e.type === "meta") f(), a.push(e), d();
	else if (e.role === "model" && e.type === "tool_call") d(), f(), s.push(e);
	else if (e.role === "model" && e.type === "compaction_start") d(), f(), m(), i.push({
		role: "model",
		type: "compaction",
		status: "compacting",
		timestamp: e.timestamp
	});
	else if (e.role === "model" && e.type === "compaction_complete") {
		d(), f(), m();
		let t = i.findIndex((e) => e.role === "model" && e.type === "compaction" && e.status === "compacting"), n = {
			role: "model",
			type: "compaction",
			status: "complete",
			summary: e.text,
			timestamp: e.timestamp
		};
		t >= 0 ? i[t] = n : i.push(n);
	} else e.role === "model" && e.type === "error" ? (d(), f(), m(), i.push(e)) : console.error("Unknown message type", e);
	d(), f(), m();
	for (let e = 0; e < i.length; e++) {
		let t = i[e];
		t.role !== "model" || t.type !== "tool_call" || t.tool_calls.some((e) => e.status === "executing") && i.slice(e + 1).some((e) => e.role === "model" && (e.type === "text" || e.type === "tool_call" || e.type === "error")) && (i[e] = {
			...t,
			tool_calls: t.tool_calls.map((e) => e.status === "executing" ? {
				...e,
				status: "success"
			} : e)
		});
	}
	return i;
}
function E(e) {
	return Object.values(e).filter((e) => e !== void 0 && e.type === "client").map((e) => ({
		name: e.name,
		description: typeof e.description == "string" ? A(e.description) : e.description,
		request_json_schema: e.requestSchema,
		json_response_schema: e.responseSchema
	}));
}
function D(e) {
	return Array.isArray(e) ? e.map((e) => A(e.instruction).trim()) : Object.values(e).filter((e) => e !== void 0).map((e) => A(e.instruction).trim());
}
function O(e) {
	return Array.isArray(e) ? e.map((e) => ({
		description: A(e.description),
		value: S(typeof e.value == "function" ? e.value() : e.value)
	})) : Object.values(e).filter((e) => e !== void 0).map((e) => ({
		description: A(e.description),
		value: S(typeof e.value == "function" ? e.value() : e.value)
	}));
}
function k(e, t) {
	if (typeof e == "function") try {
		return e(t);
	} catch {
		return !0;
	}
	return e === !0;
}
function A(e) {
	let t = e.split("\n"), n = t.filter((e) => e.trim().length > 0);
	if (n.length === 0) return e;
	let r = n.map((e) => {
		let t = e.match(/^(\s*)/);
		return t ? t[1].length : 0;
	}), i = Math.min(...r);
	return t.map((e) => e.trim().length === 0 ? e : e.slice(i)).join("\n");
}
function j(e, t) {
	for (let n of e) if (n.role === "model" && n.type === "tool_call") {
		let e = n.tool_calls.find((e) => e.id === t);
		if (e) return e;
	}
}
function M(e) {
	return e.length > 0 && e.every((e) => e.role === "user" && e.type !== "tool_response");
}
function N(e) {
	return e.filter((e) => e.role === "user" && e.type !== "tool_response");
}
function P(e) {
	return e.map((e) => {
		switch (e.role) {
			case "model": return e;
			case "user": return e.client_message_id != null && e.client_message_id.length > 0 ? e : {
				...e,
				client_message_id: r()
			};
			default: return e;
		}
	});
}
function F(e, t) {
	if (t == null) return e;
	for (let n = e.length - 1; n >= 0; n--) {
		let r = e[n];
		if (r.type === "meta" && r.meta?.id === t) return e.slice(n + 1);
	}
	return e;
}
function I(e, t) {
	if (t.length === 0 || t.length > e.length) return !1;
	for (let n = 0; n <= e.length - t.length; n++) if (t.every((t, r) => L(e[n + r], t))) return !0;
	return !1;
}
function L(e, t) {
	let n = R(e), r = R(t);
	return n != null && r != null ? n === r : e.role !== "user" || t.role !== "user" || e.type !== t.type ? !1 : e.type === "text" && t.type === "text" ? e.text === t.text : e.type === "content" && t.type === "content" ? e.content === t.content && e.mime_type === t.mime_type : JSON.stringify(z(e)) === JSON.stringify(z(t));
}
function R(e) {
	if (e.role === "user") return e.client_message_id != null && e.client_message_id.length > 0 ? e.client_message_id : void 0;
}
function z(e) {
	let { timestamp: t, ...n } = e;
	return n;
}
function B(e) {
	let t = e.get(), n = t[0], r = n?.messages ?? [], i = n?.instructions ?? [];
	if (r.length === 0) return e.set([]), {
		messages: [],
		instructions: []
	};
	e.set(t.slice(1));
	let a = r.filter((e) => e.type === "text" && "text" in e), o = [...r.filter((e) => e.type !== "text")];
	if (a.length > 0) if (a.length === 1) o.push(a[0]);
	else {
		let e = a.map((e) => "text" in e ? e.text : "").join("\n\n");
		o.push({
			role: "user",
			type: "text",
			text: e,
			timestamp: s()
		});
	}
	return {
		messages: o,
		instructions: i
	};
}
function V(e) {
	return !!(e instanceof DOMException && e.name === "AbortError" || typeof e == "object" && e && "name" in e && e.name === "CanceledError");
}
function H(e) {
	return e instanceof c ? !0 : U(e) === 409;
}
function U(e) {
	if (typeof e != "object" || !e) return;
	let t = "response" in e ? e.response : void 0;
	return typeof t == "object" && t && "status" in t && typeof t.status == "number" ? t.status : U("cause" in e ? e.cause : void 0);
}
function W(e) {
	return {
		role: "user",
		type: "tool_response",
		tool_responses: e.map((e) => ({
			tool_call_id: e.id,
			name: e.name,
			response: {
				result: { status: "rejected" },
				error: null
			},
			finished: !0
		})),
		timestamp: s()
	};
}
function G(e) {
	let t = {};
	for (let n of e) t[n.name] = {
		type: "server",
		name: n.name,
		description: n.description,
		requestSchema: n.request_json_schema,
		responseSchema: n.response_json_schema
	};
	return t;
}
//#endregion
export { c as CopilotActiveExchangeError, x as CopilotSession, u as ORPHANED_TOOL_CALL_ERROR, h as RECOVERY_POLL_CEILING_MS, m as RECOVERY_STOP_RECONCILIATION_MS, j as findToolCallById, g as nextRecoveryDelayMs, O as prepareContextForTransport, D as prepareInstructionsForTransport, E as prepareToolsForTransport, k as resolveNeedsApproval, G as serverSideToolsToCopilotTools, S as stringifyForCopilot, A as undent, b as updatedStatus };
